import { CalcTarget } from '../types';

export type ActiveCalculationType = CalcTarget | 'tijd' | 'afstand';

export interface CardThemeStyles {
  // 1. Result Card (Active calculation, Output)
  resultCard: {
    container: string;
    iconContainer: string;
    badge: string;
    number: string;
    subtext: string;
    innerBox: string;
  };
  // 2. Input Card (Inactive calculation, Input)
  inputCard: {
    container: string;
    iconContainer: string;
    activePreset: string;
    inactivePreset: string;
  };
}

/**
 * Material Design 3 theme configuration for calculation cards:
 * 
 * 1. Resultaatkaart (Actieve berekening, Output)
 * - Tijd: bg-orange-100 border-orange-200 text-orange-900
 * - Pace: bg-blue-100 border-blue-200 text-blue-900
 * - Afstand: bg-emerald-100 border-emerald-200 text-emerald-900
 * 
 * 2. Invoerkaarten (Input)
 * - Oppervlakte is ALTIJD neutraal: bg-slate-50 border border-slate-100 rounded-2xl
 * - Accentkleuren uitsluitend op icoon-container (linksboven) en actieve preset:
 *   - Tijd-invoerkaart: Icoon-container bg-orange-100 text-orange-600, actieve preset bg-orange-500 text-white shadow-sm
 *   - Pace-invoerkaart: Icoon-container bg-blue-100 text-blue-600, actieve preset bg-blue-500 text-white shadow-sm
 *   - Afstand-invoerkaart: Icoon-container bg-emerald-100 text-emerald-600, actieve preset bg-emerald-500 text-white shadow-sm
 * - Inactieve presets: bg-slate-200 text-slate-600
 */
export const themeMap: Record<CalcTarget, CardThemeStyles> = {
  time: {
    resultCard: {
      container: 'bg-orange-100 border border-orange-200 text-orange-900 dark:bg-orange-950/70 dark:border-orange-900/50 dark:text-orange-100 shadow-xs',
      iconContainer: 'bg-orange-200/80 text-orange-700 dark:bg-orange-900/60 dark:text-orange-300',
      badge: 'bg-orange-200/80 text-orange-800 dark:bg-orange-900/60 dark:text-orange-200 border border-orange-300/40 whitespace-nowrap inline-flex items-center shrink-0',
      number: 'text-orange-950 dark:text-orange-50',
      subtext: 'text-orange-800 dark:text-orange-300',
      innerBox: 'bg-white/60 dark:bg-black/25',
    },
    inputCard: {
      container: 'bg-slate-50 border border-slate-100 rounded-2xl dark:bg-slate-900/80 dark:border-slate-800 shadow-2xs',
      iconContainer: 'bg-orange-100 text-orange-600 dark:bg-orange-950/60 dark:text-orange-400',
      activePreset: 'bg-orange-500 text-white shadow-sm',
      inactivePreset: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-700',
    },
  },
  pace: {
    resultCard: {
      container: 'bg-blue-100 border border-blue-200 text-blue-900 dark:bg-blue-950/70 dark:border-blue-900/50 dark:text-blue-100 shadow-xs',
      iconContainer: 'bg-blue-200/80 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300',
      badge: 'bg-blue-200/80 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 border border-blue-300/40 whitespace-nowrap inline-flex items-center shrink-0',
      number: 'text-blue-950 dark:text-blue-50',
      subtext: 'text-blue-800 dark:text-blue-300',
      innerBox: 'bg-white/60 dark:bg-black/25',
    },
    inputCard: {
      container: 'bg-slate-50 border border-slate-100 rounded-2xl dark:bg-slate-900/80 dark:border-slate-800 shadow-2xs',
      iconContainer: 'bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400',
      activePreset: 'bg-blue-500 text-white shadow-sm',
      inactivePreset: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-700',
    },
  },
  distance: {
    resultCard: {
      container: 'bg-emerald-100 border border-emerald-200 text-emerald-900 dark:bg-emerald-950/70 dark:border-emerald-900/50 dark:text-emerald-100 shadow-xs',
      iconContainer: 'bg-emerald-200/80 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300',
      badge: 'bg-emerald-200/80 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border border-emerald-300/40 whitespace-nowrap inline-flex items-center shrink-0',
      number: 'text-emerald-950 dark:text-emerald-50',
      subtext: 'text-emerald-800 dark:text-emerald-300',
      innerBox: 'bg-white/60 dark:bg-black/25',
    },
    inputCard: {
      container: 'bg-slate-50 border border-slate-100 rounded-2xl dark:bg-slate-900/80 dark:border-slate-800 shadow-2xs',
      iconContainer: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400',
      activePreset: 'bg-emerald-500 text-white shadow-sm',
      inactivePreset: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-700',
    },
  },
};

/**
 * Helper to normalize string calculation keys (e.g. 'tijd' -> 'time', 'afstand' -> 'distance')
 */
export function normalizeCalcTarget(target: string): CalcTarget {
  if (target === 'tijd') return 'time';
  if (target === 'afstand') return 'distance';
  if (target === 'pace' || target === 'time' || target === 'distance') return target;
  return 'time';
}

/**
 * Get card styling based on whether it is the active calculation (Resultaatkaart) or an input (Invoerkaart)
 */
export function getCardTheme(type: CalcTarget, activeCalculation: CalcTarget) {
  const normalizedType = normalizeCalcTarget(type);
  const normalizedActive = normalizeCalcTarget(activeCalculation);
  const isResultCard = normalizedType === normalizedActive;

  return {
    isResultCard,
    styles: isResultCard ? themeMap[normalizedType].resultCard : themeMap[normalizedType].inputCard,
    theme: themeMap[normalizedType],
  };
}
